import React, { Fragment } from "react";

import img13 from "../../../images/service/brand/brand development.png";
import img14 from "../../../images/service/brand/brand development (2).png";
import img15 from "../../../images/service/brand/brand development (3).png";

// Service icons
import sIcon1 from "../../../images/icon/service-icon01.svg";
import sIcon2 from "../../../images/icon/service-icon02.svg";
import sIcon3 from "../../../images/icon/service-icon03.svg";
import sIcon4 from "../../../images/icon/service-icon04.svg";
import sIcon5 from "../../../images/icon/service-icon05.svg";
import sIcon6 from "../../../images/icon/service-icon06.svg";

// Common Components
import Header from "../../../components/Header/Header";
import PageTitle from "../../../components/pagetitle/PageTitle";
import Scrollbar from "../../../components/scrollbar/scrollbar";
import Footer from "../../../components/footer/Footer";

import HeroSection from "../../../components/PageSection/HeroSection";
import ServiceCardsSection from "../../../components/PageSection/ServiceCardSection";
import Benefit from "../../../components/PageSection/BenefitSection";
import KeyFeaturesSection from "../../../components/PageSection/KeyFeature";
import WhyChooseUsSection from "../../../components/PageSection/WhyChooseUsSection";
import FaqSection from "../../../components/PageSection/FAQsection";
import FinalCenteredCTA from "../../../components/PageSection/FinalCTA";
import Industries from "../../../components/Industries/Industries";
import TheorySection from "../../../components/PageSection/Theory";

// =====================
// DATA
// =====================

const serviceCards = [
  {
    icon: sIcon1,
    title: "Brand Consulting & Strategy",
    content:
      "Comprehensive brand audit, positioning strategy, target audience profiling, competitor analysis, brand architecture, and visual/verbal identity guidelines.",
  },
  {
    icon: sIcon2,
    title: "Brand Storytelling & Content",
    content:
      "Brand narrative development, origin story crafting, mission & vision messaging, tone of voice guide, and storytelling across all channels.",
  },
  {
    icon: sIcon3,
    title: "SEO & Content Marketing",
    content:
      "Keyword research, on-page & technical SEO, content strategy, link building, local SEO, schema markup, and performance tracking.",
  },
  {
    icon: sIcon4,
    title: "Online Reputation Management",
    content:
      "Brand monitoring, review management, negative content suppression, positive PR publishing, crisis management, and sentiment control.",
  },
  {
    icon: sIcon5,
    title: "Social Media Optimization",
    content:
      "Platform-specific strategy, content calendar, community management, hashtag strategy, influencer collaboration, and engagement analytics.",
  },
  {
    icon: sIcon6,
    title: "Paid Marketing & Performance",
    content:
      "Google Ads, Meta Ads, YouTube & TikTok ads, LinkedIn B2B campaigns, retargeting, conversion tracking, and ROAS optimization.",
  },
];

const benefits = [
  {
    title: "+180% Brand Awareness",
    description:
      "Average brand awareness growth through strategic positioning and multi-channel marketing campaigns.",
    icon: sIcon1,
  },
  {
    title: "3.8× Engagement Rate",
    description:
      "Higher engagement across all touchpoints through compelling storytelling and community building.",
    icon: sIcon2,
  },
  {
    title: "+65% Lead Generation",
    description:
      "Lead generation uplift within 12 months through performance-driven paid advertising and SEO.",
    icon: sIcon3,
  },
  {
    title: "Lasting Brand Equity",
    description:
      "Long-term brand value that commands premium prices and builds customer loyalty.",
    icon: sIcon4,
  },
];

const faqs = [
  {
    q: "What's included in your brand development services?",
    a: "We provide end-to-end brand building from strategy and visual identity to SEO, social media, paid advertising, and reputation management — creating comprehensive brand presence that drives business growth.",
  },
  {
    q: "How long does it take to build a brand?",
    a: "Brand foundation (strategy, identity, guidelines) typically takes 6-8 weeks. Full brand launch with digital presence, content, and marketing campaigns usually requires 12-16 weeks from discovery to market.",
  },
  {
    q: "Do you handle both creative and performance marketing?",
    a: "Yes! We blend beautiful brand design with data-driven marketing — from creative storytelling to SEO, paid ads, and conversion optimization, ensuring brands that are both memorable and measurably successful.",
  },
  {
    q: "Can you help with rebranding existing companies?",
    a: "Absolutely. We specialize in strategic rebranding projects — conducting brand audits, repositioning strategies, visual refreshes, and managing seamless transitions that preserve brand equity while driving renewal.",
  },
];

// =====================
// PAGE COMPONENT
// =====================

export default function BrandDevelopment() {
  return (
    <Fragment>
      <Header />

      <PageTitle
        pageTitle="Brand Development"
        pagesub="Strategic brand building that drives business growth"
      />

      {/* HERO SECTION */}
      <HeroSection
        title={
          <>
            <span className="gradient-text">Brand Development</span>{" "}
            That Burns Bright
          </>
        }
        subtitle={`We create unforgettable identities, powerful stories, and unstoppable digital 
presence that turn audiences into loyal communities and businesses into market leaders 
through strategic positioning, compelling storytelling, and multi-channel marketing.`}
        buttonText="Build Your Brand"
        buttonLink="/contact"
        image={img13}
        imageAlt="Brand Development Excellence"
      />

      {/* ================= THEORY 1 ================= */}
      <TheorySection
        title="Why Brand Development Drives Business Success"
        image={img14}
        paragraphs={[
          "In crowded markets where attention is scarce and competition is fierce, a strong brand isn't optional — it's essential. We craft brands that stand out, resonate deeply with target audiences, and drive measurable business growth.",
          "Strong brands command premium prices, attract top talent, build customer loyalty, and accelerate growth. In digital-first markets, your brand's online presence, reputation, and visibility directly impact revenue. Our holistic approach combines brand strategy with performance marketing to deliver both awareness and measurable ROI.",
        ]}
      />

      {/* SERVICES */}
      <ServiceCardsSection
        title="Our Brand Development Services"
        cards={serviceCards}
      />

      {/* ================= THEORY 2 ================= */}
      <TheorySection
        title="Our Brand Building Journey"
        image={img15}
        paragraphs={[
          "Discovery & Strategy: Brand audit, competitor benchmarking, audience deep dive, core brand promise & values, and positioning & differentiation strategy that makes you unforgettable.",
          "Identity Creation: Logo & visual identity, color palette, typography, brand voice & tone, comprehensive brand guidelines, and touchpoint design across all channels.",
          "Storytelling & Launch: Brand narrative development, website & digital assets, launch campaign strategy, content pillars, and integrated social & PR rollout that creates momentum.",
          "Growth & Amplification: SEO & content marketing, paid campaigns across channels, social media scaling, community building, performance tracking, and continuous iteration for sustained growth.",
        ]}
      />

      {/* BENEFITS */}
      <Benefit
        title="Results That Ignite Brands"
        benefits={benefits}
      />

      {/* KEY FEATURES */}
      <KeyFeaturesSection
        title="Key Pillars of Our Brand Development"
        subtitle="Comprehensive brand building from strategy to scale"
        features={[
          {
            icon: sIcon1,
            title: "Strategic Brand Positioning",
            description:
              "Clear positioning and differentiation that makes your brand unforgettable in competitive markets.",
          },
          {
            icon: sIcon2,
            title: "Compelling Brand Storytelling",
            description:
              "Emotional narratives that create deep connections and turn customers into brand advocates.",
          },
          {
            icon: sIcon3,
            title: "Omnichannel Brand Presence",
            description:
              "Consistent brand experience across web, social, paid ads, SEO, and all customer touchpoints.",
          },
          {
            icon: sIcon4,
            title: "Data-Driven Marketing",
            description:
              "Beautiful creative backed by analytics and performance tracking for measurable business impact.",
          },
          {
            icon: sIcon5,
            title: "Reputation Management",
            description:
              "Proactive brand monitoring, review management, and crisis response to protect and enhance brand perception.",
          },
          {
            icon: sIcon6,
            title: "Performance & Growth",
            description:
              "SEO, paid advertising, and conversion optimization driving 180% brand awareness and 65%+ lead generation growth.",
          },
        ]}
      />

      <Industries />

      {/* WHY CHOOSE US */}
      <WhyChooseUsSection
        items={[
          "Full-spectrum brand building from strategy to execution",
          "Data-driven creative excellence with measurable ROI",
          "Omnichannel expertise across all digital touchpoints",
          "Proven results: 180% brand awareness, 3.8× engagement rates",
        ]}
      />

      {/* FAQ */}
      <FaqSection faqs={faqs} />

      {/* FINAL CTA */}
      <FinalCenteredCTA />

      <Footer />
      <Scrollbar />
    </Fragment>
  );
}

